import React from "react";

/*
  14 EU allergen icons — inline minimal SVGs (Reg. UE 1169/2011).
  Each icon: colored circle background + white line glyph.
  Rendered at any size via the `size` prop; base viewBox 32×32.
*/

const A = ({ children, bg, size = 26, title }) => (
  <span
    role="img"
    aria-label={title}
    title={title}
    style={{ width: size, height: size }}
    className="inline-flex items-center justify-center shrink-0"
  >
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="16" cy="16" r="15" fill={bg} stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
      {children}
    </svg>
  </span>
);

const S = { fill: "none", stroke: "#fff", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };

const ICONS = {
  1: (size, title) => (
    // Wheat / Gluten
    <A bg="#b8842a" size={size} title={title}>
      <path {...S} d="M16 7v18" />
      <path {...S} d="M16 11c-2 0-3.4 1-4 2.6.6 1.6 2 2.4 4 2.4M16 11c2 0 3.4 1 4 2.6-.6 1.6-2 2.4-4 2.4" />
      <path {...S} d="M16 16c-2 0-3.4 1-4 2.6.6 1.6 2 2.4 4 2.4M16 16c2 0 3.4 1 4 2.6-.6 1.6-2 2.4-4 2.4" />
      <path {...S} d="M16 21c-2 0-3.4 1-4 2.6M16 21c2 0 3.4 1 4 2.6" />
    </A>
  ),
  2: (size, title) => (
    // Crab / Crustaceans
    <A bg="#a83232" size={size} title={title}>
      <ellipse {...S} cx="16" cy="17" rx="7" ry="4" />
      <path {...S} d="M10 15l-2-2M8 13l1-2M22 15l2-2M24 13l-1-2" />
      <path {...S} d="M12 20l-2 3M20 20l2 3" />
      <circle cx="13" cy="16" r="0.9" fill="#fff" />
      <circle cx="19" cy="16" r="0.9" fill="#fff" />
    </A>
  ),
  3: (size, title) => (
    // Egg
    <A bg="#d4a03a" size={size} title={title}>
      <path {...S} d="M16 8c-4 0-6 5-6 9a6 6 0 0012 0c0-4-2-9-6-9z" />
      <circle cx="16" cy="17" r="2.5" fill="#fff" />
      <circle cx="16" cy="17" r="1.2" fill="#d4a03a" />
    </A>
  ),
  4: (size, title) => (
    // Fish
    <A bg="#2a2a2a" size={size} title={title}>
      <path {...S} d="M7 16c3-4 8-5 12-5 3 0 5 2 6 5-1 3-3 5-6 5-4 0-9-1-12-5z" />
      <path {...S} d="M7 16l-3-2v4z" />
      <circle cx="22" cy="15" r="0.9" fill="#fff" />
    </A>
  ),
  5: (size, title) => (
    // Peanut
    <A bg="#b58555" size={size} title={title}>
      <path {...S} d="M13 10c-2 1-3 3-2 5 1 1 2 1 3 2s2 3 1 5c-1 2-3 2-4 1" />
      <ellipse {...S} cx="14" cy="11.5" rx="3.2" ry="3" />
      <ellipse {...S} cx="18" cy="20.5" rx="3.2" ry="3" />
      <path {...S} d="M14 15l1 2M17 15l-1 2" />
    </A>
  ),
  6: (size, title) => (
    // Soy pod
    <A bg="#5f8a3d" size={size} title={title}>
      <path {...S} d="M8 20c2-8 10-12 16-10-2 8-10 12-16 10z" />
      <circle cx="14" cy="16" r="1.4" fill="#fff" />
      <circle cx="18" cy="14" r="1.4" fill="#fff" />
      <circle cx="12" cy="18" r="1.2" fill="#fff" />
    </A>
  ),
  7: (size, title) => (
    // Milk carton
    <A bg="#3a7ab8" size={size} title={title}>
      <path {...S} d="M12 10h8v2l1 2v9a1 1 0 01-1 1h-8a1 1 0 01-1-1v-9l1-2z" />
      <path {...S} d="M12 10l-1 2h10l-1-2" />
      <rect x="14" y="17" width="4" height="4" rx="0.5" {...S} />
    </A>
  ),
  8: (size, title) => (
    // Walnut / Tree nut
    <A bg="#7a5230" size={size} title={title}>
      <circle {...S} cx="16" cy="16" r="7" />
      <path {...S} d="M16 9v14M11 12c2 1 3 3 3 5s-1 4-3 5M21 12c-2 1-3 3-3 5s1 4 3 5" />
    </A>
  ),
  9: (size, title) => (
    // Celery
    <A bg="#4f8a4a" size={size} title={title}>
      <path {...S} d="M14 24V13c0-2 1-3 2-3s2 1 2 3v11" />
      <path {...S} d="M13 13c-2 0-3-2-2-4M19 13c2 0 3-2 2-4M16 10c0-2 1-3 3-3M16 10c0-2-1-3-3-3" />
    </A>
  ),
  10: (size, title) => (
    // Mustard bottle
    <A bg="#c9a534" size={size} title={title}>
      <path {...S} d="M14 9h4v3l1 2v9a1 1 0 01-1 1h-4a1 1 0 01-1-1v-9l1-2z" />
      <path {...S} d="M14 12h4" />
      <path {...S} d="M15 17h2" />
    </A>
  ),
  11: (size, title) => (
    // Sesame seeds
    <A bg="#c9b57a" size={size} title={title}>
      <ellipse cx="12" cy="14" rx="1.8" ry="2.8" fill="#fff" transform="rotate(-20 12 14)" />
      <ellipse cx="20" cy="14" rx="1.8" ry="2.8" fill="#fff" transform="rotate(20 20 14)" />
      <ellipse cx="16" cy="20" rx="1.8" ry="2.8" fill="#fff" />
      <ellipse cx="10" cy="20" rx="1.6" ry="2.4" fill="#fff" transform="rotate(-30 10 20)" />
      <ellipse cx="22" cy="20" rx="1.6" ry="2.4" fill="#fff" transform="rotate(30 22 20)" />
    </A>
  ),
  12: (size, title) => (
    // SO2 sulfites
    <A bg="#2a2a2a" size={size} title={title}>
      <text
        x="16"
        y="19"
        textAnchor="middle"
        fontFamily="Outfit, sans-serif"
        fontSize="10"
        fontWeight="700"
        fill="#fff"
      >
        SO
        <tspan fontSize="7" baselineShift="sub">2</tspan>
      </text>
    </A>
  ),
  13: (size, title) => (
    // Lupin bean
    <A bg="#a68855" size={size} title={title}>
      <ellipse {...S} cx="13" cy="14" rx="3" ry="2.5" />
      <ellipse {...S} cx="19" cy="14" rx="3" ry="2.5" />
      <ellipse {...S} cx="16" cy="20" rx="3" ry="2.5" />
    </A>
  ),
  14: (size, title) => (
    // Mollusc shell
    <A bg="#4d8ac0" size={size} title={title}>
      <path {...S} d="M8 22c2-9 6-13 8-13s6 4 8 13z" />
      <path {...S} d="M12 22l1-9M16 22V9M20 22l-1-9M10 22c1-6 3-9 3-9M22 22c-1-6-3-9-3-9" />
    </A>
  ),
};

export const AllergenIcon = ({ id, size = 26, title = "" }) =>
  ICONS[id] ? ICONS[id](size, title) : null;

export default AllergenIcon;
